import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import NotificationBell from "@/components/NotificationBell";
import LanguageToggle from "@/components/LanguageToggle";
import TemperatureToggle from "@/components/TemperatureToggle";
import FamilySwitcher from "@/components/FamilySwitcher";
import {
  LayoutDashboard, AlertTriangle, FileText, MessagesSquare, BookOpen, CalendarClock, BarChart3, Settings as SettingsIcon, Leaf, LogOut, Menu, X,
} from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/app", key: "dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/incidents", key: "incidents", icon: AlertTriangle, testid: "nav-incidents" },
  { to: "/app/compliance", key: "compliance", icon: CalendarClock, testid: "nav-compliance" },
  { to: "/app/documents", key: "documents", icon: FileText, testid: "nav-documents" },
  { to: "/app/assistant", key: "assistant", icon: MessagesSquare, testid: "nav-assistant" },
  { to: "/app/knowledge", key: "knowledge", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/app/reports", key: "reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/app/settings", key: "settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarBody = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-6 h-16 border-b border-[#DCE3DD]">
        <div className="h-8 w-8 grid place-items-center bg-[#275C3F] rounded-sm">
          <Leaf className="h-5 w-5 text-white" />
        </div>
        <span className="font-display font-bold text-lg text-[#121A14]">EnviSafe<span className="text-[#275C3F]">.ai</span></span>
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-[#275C3F] text-white"
                  : "text-[#4B5950] hover:bg-[#EAF0EA] hover:text-[#275C3F]"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" />
            {t(`nav.${n.key}`)}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-[#DCE3DD]">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="h-9 w-9 rounded-full bg-[#EAF0EA] grid place-items-center overflow-hidden shrink-0">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-[#275C3F] font-semibold text-sm">
                {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#121A14] truncate">{user?.name || "User"}</p>
            <p className="text-xs text-[#4B5950] truncate">{user?.email}</p>
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={onLogout}
          className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-[#B84A22] hover:bg-[#B84A22]/10 transition-all duration-200"
        >
          <LogOut className="h-4 w-4" /> {t("common.signOut")}
        </button>
        <a
          href="https://worksafeagent.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sidebar-family-link"
          className="mt-2 block px-3 text-[11px] text-[#9CA99F] hover:text-[#275C3F] transition-colors"
        >
          Part of the WorkSafe family ↗
        </a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAF8]">
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-[#DCE3DD] fixed inset-y-0">
        <SidebarBody />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-white border-r border-[#DCE3DD]">
            <SidebarBody />
          </div>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="h-16 bg-white border-b border-[#DCE3DD] flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-2">
            <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} className="p-2 lg:hidden">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div className="flex items-center gap-2 lg:hidden">
              <Leaf className="h-5 w-5 text-[#275C3F]" />
              <span className="font-display font-bold">EnviSafe.ai</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <FamilySwitcher />
            <TemperatureToggle />
            <LanguageToggle />
            <NotificationBell />
          </div>
        </header>
        <main className="p-5 md:p-8 max-w-[1400px]">{children}</main>
      </div>
    </div>
  );
}
